import React, { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthReducer } from '@/hooks/useAuthReducer';
import './Login.css';

interface LoginFormState {
  username: string;
  password: string;
  formError: string;
}

type LoginFormAction =
  | { type: 'SET_USERNAME'; payload: string }
  | { type: 'SET_PASSWORD'; payload: string }
  | { type: 'SET_FORM_ERROR'; payload: string }
  | { type: 'CLEAR_FORM_ERROR' }
  | { type: 'CLEAR_PASSWORD' };

const loginFormReducer = (state: LoginFormState, action: LoginFormAction): LoginFormState => {
  switch (action.type) {
    case 'SET_USERNAME':
      return { ...state, username: action.payload };
    case 'SET_PASSWORD':
      return { ...state, password: action.payload };
    case 'SET_FORM_ERROR':
      return { ...state, formError: action.payload };
    case 'CLEAR_FORM_ERROR':
      return { ...state, formError: '' };
    case 'CLEAR_PASSWORD':
      return { ...state, password: '' };
    default:
      return state;
  }
};

const initialLoginFormState: LoginFormState = {
  username: '',
  password: '',
  formError: '',
};

const Login: React.FC = () => {
  const [formState, formDispatch] = useReducer(loginFormReducer, initialLoginFormState);
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error: authError } = useAuthReducer();

  const displayError = formState.formError || authError;

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    formDispatch({ type: 'CLEAR_FORM_ERROR' });

    if (!formState.username.trim() || !formState.password.trim()) {
      formDispatch({ type: 'SET_FORM_ERROR', payload: 'Por favor, preencha todos os campos.' });
      return;
    }

    try {
      const success = await login({ username: formState.username.trim(), password: formState.password });
      
      if (success) {
        navigate('/admin');
      } else {
        formDispatch({ type: 'CLEAR_PASSWORD' });
      }
    } catch (error) {
      console.error('Login error:', error);
      formDispatch({ type: 'SET_FORM_ERROR', payload: 'Erro interno. Tente novamente mais tarde.' });
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-titulo">
          <h1>Painel Admin</h1>
          <p>Faça login para acessar o painel de controle</p>
        </div>
        
        {displayError && (
          <div className="error-message">
            <p>{displayError}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Usuário:</label>
            <input
              type="text"
              id="username"
              value={formState.username}
              onChange={(e) => formDispatch({ type: 'SET_USERNAME', payload: e.target.value })}
              required
              disabled={isLoading}
              autoComplete="username"
              placeholder="Digite seu usuário"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha:</label>
            <input
              type="password"
              id="password"
              value={formState.password}
              onChange={(e) => formDispatch({ type: 'SET_PASSWORD', payload: e.target.value })}
              required
              disabled={isLoading}
              autoComplete="current-password"
              placeholder="Digite sua senha"
            />
          </div>

          <button type="submit" disabled={isLoading || !formState.username.trim() || !formState.password.trim()}>
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </button>
        </form>
        
        <div className="login-info">
          <p><small>
            Use as credenciais fornecidas pelo administrador para acessar o painel.
          </small></p>
        </div>
      </div>
    </div>
  );
};

export default Login;